import React from 'react'
import { isAlive } from 'mobx-state-tree'
import { Input } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { StoreType } from 'polotno/model/store'
import { ImagesGrid, SectionTab } from 'polotno/side-panel'
import { API } from 'polotno/utils/api'
import { t } from 'polotno/utils/l10n'
import { useInfiniteAPI } from './customTemplatePanel'
import { getKey } from 'polotno/utils/validate-key'
import { svgToURL } from 'polotno/utils/svg'
import styled from 'polotno/utils/styled'
import { figureToSvg, TYPES } from 'polotno/utils/figure-to-svg'
import { BulbIcon } from '../hugeIcons/hugeIcons'
import './customElementsPanel.less'

// ─── Noun Project icon helper ─────────────────────────────────────────────────

const iconToSrc = async (id: any) => {
  const req    = await fetch(`${API}/download-nounproject?id=${id}&KEY=${getKey()}`)
  const text   = await req.text()
  const base64 = await svgToURL(text)
  return base64
}

const NounContainer = styled('div')`
  height: 100%;
  overflow: hidden;
`

const limit = 50

export const NounprojectPanel = ({ store, query }: any) => {
  const { data, isLoading, loadMore, setQuery, hasMore } = useInfiniteAPI({
    defaultQuery: query,
    getAPI: ({ page, query }) =>
      `${API}/get-nounproject?query=${query}&page=${page}&limit=${limit}&KEY=${getKey()}`,
    getSize: (res) => res.pagesNumber,
  })

  React.useEffect(() => { setQuery(query) }, [query])

  return (
    <NounContainer className="elements-panel__icon-grid">
      <ImagesGrid
        shadowEnabled={false}
        images={data?.map((d) => d.icons).flat()}
        getPreview={(item) => item.preview_url_84}
        isLoading={isLoading}
        onSelect={async (item, pos, element) => {
          if (element && element.type === 'image' && element.contentEditable) {
            const src = await iconToSrc(item.id)
            element.set({ clipSrc: src })
            return
          }
          const width = 200, height = 200
          store.history.transaction(async () => {
            const x   = (pos?.x || store.width / 2) - width / 2
            const y   = (pos?.y || store.height / 2) - height / 2
            const svg = store.activePage?.addElement({ type: 'svg', width, height, x, y })
            const src = await iconToSrc(item.id)
            if (isAlive(svg)) await svg.set({ src })
          })
        }}
        rowsNumber={4}
        loadMore={hasMore && loadMore}
      />
    </NounContainer>
  )
}

// ─── Lines ────────────────────────────────────────────────────────────────────

const LINES = [
  { preview: svgToURL`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="16"><path stroke="currentColor" strokeWidth="4" d="M 1 8 L 30 8"></path></svg>`, data: {} },
  { preview: svgToURL`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="16"><path stroke="currentColor" strokeWidth="4" stroke-dasharray="4 2" d="M 1 8 L 30 8"></path></svg>`, data: { dash: [4, 2] } },
  { preview: svgToURL`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="16"><path stroke="currentColor" strokeWidth="4" stroke-dasharray="1 1" d="M 1 8 L 30 8"></path></svg>`, data: { dash: [1, 1] } },
  {
    preview: svgToURL`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="16"><path stroke="currentColor" strokeWidth="2" d="M 25 6 L 29 8 L 25 10" fill="none" strokeLinejoin="round" strokeLinecap="round"></path><path stroke="currentColor" strokeWidth="4" d="M 1 8 L 29 8" strokeLinejoin="round" strokeLinecap="round"></path></svg>`,
    data: { endHead: 'arrow' },
  },
  {
    preview: svgToURL`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="16"><path stroke="currentColor" strokeWidth="2" d="M 25 6 L 29 8 L 25 10 Z" fill="currentColor" strokeLinejoin="round" strokeLinecap="round"></path><path stroke="currentColor" strokeWidth="4" d="M 1 8 L 29 8" strokeLinejoin="round" strokeLinecap="round"></path><circle cx="3" cy="8" r="2" fill="currentColor"></circle></svg>`,
    data: { startHead: 'circle', endHead: 'arrow' },
  },
  {
    preview: svgToURL`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="16"><path stroke="currentColor" strokeWidth="2" d="M 1 6 L 5 6 L 5 10 L 1 10 Z" fill="currentColor" strokeLinejoin="round" strokeLinecap="round"></path><path stroke="currentColor" strokeWidth="4" d="M 1 8 L 29 8" strokeLinejoin="round" strokeLinecap="round" stroke-dasharray="2 1"></path><path stroke="currentColor" strokeWidth="4" d="M 29 6 L 29 10" strokeLinejoin="round" strokeLinecap="round"></path></svg>`,
    data: { startHead: 'circle', endHead: 'bar', dash: [2, 1] },
  },
]

const Lines = ({ store }: any) => (
  <ImagesGrid
    shadowEnabled={false}
    rowsNumber={3}
    images={LINES}
    getPreview={(image) => image.preview}
    itemHeight={50}
    isLoading={false}
    onSelect={async (image, pos) => {
      const width = store.activePage.computedWidth / 3
      store.activePage.addElement({
        type: 'line',
        x: pos ? pos.x : store.activePage.computedWidth  / 2 - width / 2,
        y: pos ? pos.y : store.activePage.computedHeight / 2,
        width,
        ...image.data,
      })
    }}
  />
)

// ─── Shapes ───────────────────────────────────────────────────────────────────

const FIGURE_DEFAULTS = {
  width:       300,
  height:      300,
  fill:        'black',
  stroke:      '#0c0c0c',
  strokeWidth: 0,
  url:         '',
}

const subTypes = Object.keys(TYPES)
const FIGURES: any[] = []
subTypes.forEach((subType) => {
  FIGURES.push({ subType, ...FIGURE_DEFAULTS })
})
FIGURES.forEach((figure) => {
  figure.url = svgToURL(figureToSvg(figure))
})

const BasicShapes = ({ store }: any) => {
  const rowsNumber = Math.ceil(FIGURES.length / 4) || 1
  return (
    <div style={{ height: rowsNumber * 80 + 'px' }}>
      <ImagesGrid
        shadowEnabled={false}
        rowsNumber={4}
        images={FIGURES}
        getPreview={(image) => image.url}
        isLoading={false}
        itemHeight={80}
        onSelect={async (image, pos, element) => {
          if (element && element.type === 'image' && element.contentEditable) {
            element.set({ clipSrc: image.url })
            return
          }
          const baseSize    = 1080 + 1080
          const currentSize = store.activePage.computedWidth + store.activePage.computedHeight
          const scale       = currentSize / baseSize
          const width  = image.width  * scale
          const height = image.height * scale
          const x = (pos?.x || store.activePage.computedWidth  / 2) - width  / 2
          const y = (pos?.y || store.activePage.computedHeight / 2) - height / 2
          store.activePage?.addElement({ type: 'figure', ...image, x, y, width, height })
        }}
      />
    </div>
  )
}

// ─── Shapes + Lines combined ──────────────────────────────────────────────────

export const Shapes = ({ store }: any) => (
  <div className="elements-panel__body">
    <p className="elements-panel__section-title">{t('sidePanel.lines')}</p>
    <div className="elements-panel__lines-grid">
      <Lines store={store} />
    </div>

    <p className="elements-panel__section-title">{t('sidePanel.shapes')}</p>
    <div className="elements-panel__shapes-grid">
      <BasicShapes store={store} />
    </div>
  </div>
)

// ─── Main panel ───────────────────────────────────────────────────────────────

export const ElementsPanel = ({ store }: { store: StoreType }) => {
  const timeoutRef = React.useRef<any>()
  const [query,        setQuery]        = React.useState('')
  const [delayedQuery, setDelayedQuery] = React.useState(query)

  React.useEffect(() => {
    timeoutRef.current = setTimeout(() => setDelayedQuery(query), 500)
    return () => clearTimeout(timeoutRef.current)
  }, [query])

  const hasSearch = !!delayedQuery

  return (
    <div className="elements-panel">
      {/* Search */}
      <div className="elements-panel__search-wrap">
        <Input
          className="elements-panel__search"
          prefix={<SearchOutlined style={{ fontSize: 13 }} />}
          placeholder={t('sidePanel.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
        />
      </div>

      {/* Content */}
      {hasSearch
        ? <NounprojectPanel query={delayedQuery} store={store} />
        : <Shapes store={store} />
      }
    </div>
  )
}

// ─── Panel registration ───────────────────────────────────────────────────────

export const CustomElementPolotno = {
  name: 'element-panel',
  Tab: (props: any) => (
    <SectionTab name="Element" {...props}>
      <BulbIcon className="sidebar-panel-tab-icon" />
    </SectionTab>
  ),
  Panel: ElementsPanel,
}
